/**
 * Separator-less trading-pair splitting — the SINGLE source for turning a
 * concatenated symbol (`BTCUSDT`, `ETHBUSD`) into `{ base, quote }` via a
 * known-quote suffix list.
 *
 * Four consumers share this:
 *   - `pattern-matching.service.ts` `extractBase`/`extractQuote` transforms
 *     (complex-rules templates).
 *   - `api-pattern-matcher.service.ts` — the same two transforms in the server
 *     preview, for the (universal) case where no `symbolParsing` is configured.
 *   - `clientSideTransformer` — the same two transforms in the in-browser live
 *     preview. It carried a NAIVE 3/4-character split instead, so `BTCUSDT`
 *     previewed as base `BTCU` / quote `SDT` while every backend engine wrote
 *     `BTC` / `USDT`. Binance, Binance Futures and Bybit all ship `extractBase`.
 *   - `v2-rule-processor.service.ts` `splitPair` — the basic pair-trade path,
 *     when the template declares `pairSeparator: 'none'` (the UI's explicit
 *     "no delimiter" option) or when no delimiter is found in the symbol.
 * It previously lived (unexported) only inside pattern-matching, so V2's basic
 * path had NO way to split a delimiter-less symbol and silently dropped every
 * such trade to `__unknown__`. It lives in `@bat/shared` because the browser is
 * one of the consumers and must not fork it again. No runtime deps.
 */

/**
 * Known quote currencies, MOST-SPECIFIC FIRST. Order is correctness-critical:
 * a quote that is a suffix of another (`USD` is a suffix of `USDT`/`USDC`/`BUSD`)
 * MUST come AFTER the longer ones, or a greedy `endsWith` match truncates the
 * base. `ETHBUSD` matched `USD` before `BUSD` here and split to base `ETHB` /
 * quote `USD` — a phantom asset and the wrong quote (tax-critical). The 4-char
 * USD-family quotes (`USDT`, `USDC`, `BUSD`) therefore precede bare `USD`.
 */
export const KNOWN_QUOTE_CURRENCIES = [
  'USDT',
  'USDC',
  'BUSD',
  'USD',
  'BTC',
  'ETH',
  'BNB',
  'DAI',
  'EUR',
  'GBP',
] as const

/**
 * Find where a known quote currency abuts the symbol boundary and return the
 * POSITIONAL split `[leadingChunk, trailingChunk]` (NOT semantic base/quote —
 * the caller assigns those via the template's `pairOrder`). Returns `null` when
 * no known quote is found at either probed end.
 *
 * `quoteFirst` picks which end to probe FIRST (the other is a fallback):
 *   - `false` (base-quote, the common case): quote is the SUFFIX
 *     — Binance `BTCUSDT` → `['BTC', 'USDT']`.
 *   - `true` (quote-base): quote is the PREFIX
 *     — Poloniex `BTCXMR` → `['BTC', 'XMR']`, `USDTDOT` → `['USDT', 'DOT']`.
 *     The suffix-only splitter could not handle these: the quote (`BTC`/`USDT`)
 *     is the LEADING symbol and the base (`XMR`/`DOT`) is not itself a known
 *     quote, so `endsWith` never matched and every such trade dropped to
 *     `__unknown__` (tax-critical — a whole exchange's trades silently lost).
 */
function findKnownQuoteSplit(symbol: string, quoteFirst: boolean): [string, string] | null {
  // Bitget-style product suffix: `XXXUSDT_SPBL` / `BTCUSDT_UMCBL` glue a market
  // PRODUCT code (spot / usdt-margined / coin-margined) onto a concatenated pair
  // with an underscore. The symbol then no longer ENDS in a known quote
  // (`…USDT_SPBL`), so the probes below miss it and the trade drops to
  // `__unknown__`. Strip a trailing `_<segment>` when that segment is NOT itself
  // a known quote — so a genuine underscore-DELIMITED `BTC_USDT` (whose last
  // segment IS a quote) is left untouched for the `_` separator path. No-op for
  // a plain concatenated symbol (`BTCUSDT`, no underscore).
  const underscoreIndex = symbol.lastIndexOf('_')
  const trailingSegment = underscoreIndex >= 0 ? symbol.slice(underscoreIndex + 1) : ''
  const probe =
    underscoreIndex > 0 &&
    !KNOWN_QUOTE_CURRENCIES.some((quote) => quote === trailingSegment.toUpperCase())
      ? symbol.slice(0, underscoreIndex)
      : symbol
  // A symbol that IS exactly a known quote ('USDT', 'BTC') is not a pair at
  // all — never split it. The per-quote length guard below only excludes the
  // SAME quote; without this check a SHORTER quote still matches ('USDT'
  // startsWith 'USD' → ['USD', 'T'], a phantom 1-char asset).
  if (KNOWN_QUOTE_CURRENCIES.some((quote) => quote === probe)) {
    return null
  }
  const probeSuffix = (): [string, string] | null => {
    for (const quote of KNOWN_QUOTE_CURRENCIES) {
      if (probe.length > quote.length && probe.endsWith(quote)) {
        return [trimResidualDelimiters(probe.slice(0, -quote.length)), quote]
      }
    }
    return null
  }
  const probePrefix = (): [string, string] | null => {
    for (const quote of KNOWN_QUOTE_CURRENCIES) {
      if (probe.length > quote.length && probe.startsWith(quote)) {
        return [quote, trimResidualDelimiters(probe.slice(quote.length))]
      }
    }
    return null
  }
  return quoteFirst ? (probePrefix() ?? probeSuffix()) : (probeSuffix() ?? probePrefix())
}

/**
 * Strip delimiter characters left at the split boundary. This function is the
 * LAST-RESORT path for a symbol whose configured separator was not found —
 * e.g. a template configured `/` for Huobi `BNB/USDT` fed a `BTC-USDT` row
 * from another file vintage. The known-quote probe matches `USDT` but the
 * other chunk keeps the foreign delimiter (`BTC-`), a phantom asset that
 * corrupts cost-basis lots. No real ticker starts or ends with a delimiter,
 * so trimming is safe.
 */
const RESIDUAL_DELIMITERS = /^[/\-_.:\s]+|[/\-_.:\s]+$/g
function trimResidualDelimiters(chunk: string): string {
  return chunk.replace(RESIDUAL_DELIMITERS, '')
}

/**
 * Split a separator-less trading-pair symbol into the POSITIONAL pair
 * `[leadingChunk, trailingChunk]`, probing for a known quote at the end implied
 * by `quoteFirst`. The caller maps positional → semantic base/quote via its
 * `pairOrder` swap (exactly as it does for a delimited symbol), so this stays
 * order-agnostic. Falls back to `[symbol, '']` when no known quote is found.
 */
export function splitConcatenatedSymbol(symbol: string, quoteFirst: boolean): [string, string] {
  return findKnownQuoteSplit(symbol, quoteFirst) ?? [symbol, '']
}

/**
 * Split a separator-less symbol into SEMANTIC `{ base, quote }` assuming the
 * base-quote (suffix) convention. Kept for the pattern-matching transforms
 * (`extractBase`/`extractQuote`), which have no `pairOrder` context. Returns
 * `quote: ''` when no known quote suffix matches (caller keeps whole as base).
 */
export function splitSymbolByKnownQuote(symbol: string): { base: string; quote: string } {
  const split = findKnownQuoteSplit(symbol, false)
  return split ? { base: split[0], quote: split[1] } : { base: symbol, quote: '' }
}
